<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisSelect from 'dashboard/components-next/LaisSelect.vue';
import { input } from '../utils/minhaLaisOptions';
import {
  formatDateTime,
  whatsappDisplayNameStatusClass,
  whatsappDisplayNameStatusLabel,
} from '../utils/minhaLaisPresenters';

defineProps({
  whatsappConnectionOptions: { type: Array, default: () => [] },
  whatsappPhotoFile: { type: Object, default: null },
  isLoadingWhatsappGovernance: { type: Boolean, default: false },
  isSavingWhatsappProfile: { type: Boolean, default: false },
  isUploadingWhatsappPhoto: { type: Boolean, default: false },
  isRequestingWhatsappDisplayName: { type: Boolean, default: false },
});

const emit = defineEmits([
  'save',
  'selectPhoto',
  'uploadPhoto',
  'requestDisplayName',
]);

const whatsappGovernance = defineModel({
  type: Object,
  required: true,
});

const whatsappRequestedDisplayName = defineModel(
  'whatsappRequestedDisplayName',
  { type: String, default: '' }
);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisCard title="Perfil do WhatsApp">
    <div
      v-if="isLoadingWhatsappGovernance"
      class="flex items-center gap-2 rounded-lais border border-[#e5e7eb] bg-[#faf8fd] p-4 text-[13px] text-[#6b7280]"
    >
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      Carregando perfil...
    </div>

    <div v-else class="grid gap-4">
      <LaisField label="Conexão principal">
        <LaisSelect
          v-model="whatsappGovernance.connection_id"
          :options="whatsappConnectionOptions"
          placeholder="Selecione uma conexão"
          searchable
        />
      </LaisField>

      <LaisField label="Descrição" helper="Até 512 caracteres">
        <textarea
          v-model="whatsappGovernance.description"
          rows="4"
          maxlength="512"
          :class="input"
        />
        <p class="mt-1 text-right text-[11px] text-[#8a8793]">
          {{ whatsappGovernance.description?.length || 0 }}/512
        </p>
      </LaisField>

      <div class="grid gap-4 lg:grid-cols-2">
        <LaisField label="Sobre">
          <input
            v-model="whatsappGovernance.about"
            type="text"
            maxlength="139"
            :class="input"
          />
        </LaisField>

        <LaisField label="E-mail de contato">
          <input
            v-model="whatsappGovernance.contact_email"
            type="email"
            :class="input"
          />
        </LaisField>
      </div>

      <LaisField label="Endereço">
        <input
          v-model="whatsappGovernance.address"
          type="text"
          :class="input"
        />
      </LaisField>

      <LaisField
        label="Site"
        helper="Alteração feita via suporte para evitar reprovação Meta."
      >
        <input
          :value="whatsappGovernance.website_url || 'Via suporte'"
          type="text"
          :class="input"
          readonly
        />
      </LaisField>

      <div class="rounded-lais border border-[#e5e7eb] bg-white p-4">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p class="text-[13px] font-semibold text-lais-text">
              Foto do cadastro
            </p>
            <p class="mt-1 text-[12px] text-[#6b7280]">
              Esta imagem fica no cadastro da Imudei. O gateway atual não altera
              a foto da conta no WhatsApp.
            </p>
            <p class="mt-1 text-[12px] text-[#6b7280]">
              {{
                whatsappGovernance.profile_photo?.attached
                  ? whatsappGovernance.profile_photo.filename
                  : 'Nenhuma foto enviada'
              }}
            </p>
            <p
              v-if="whatsappGovernance.last_photo_change_at"
              class="mt-1 text-[12px] text-[#8a8793]"
            >
              Último envio:
              {{ formatDateTime(whatsappGovernance.last_photo_change_at) }}
            </p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept="image/png,image/jpeg"
              class="max-w-[220px] text-[12px] text-lais-text-body"
              @change="emit('selectPhoto', $event)"
            />
            <button
              type="button"
              class="inline-flex h-10 items-center gap-2 rounded-lais-sm border border-[#e5e7eb] bg-white px-3 text-[12px] font-semibold text-lais-primary transition-colors hover:border-lais-primary/50 disabled:cursor-not-allowed disabled:opacity-60"
              :disabled="isUploadingWhatsappPhoto || !whatsappPhotoFile"
              @click="emit('uploadPhoto')"
            >
              <span
                class="size-3.5"
                :class="
                  isUploadingWhatsappPhoto
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-upload'
                "
              />
              Enviar foto
            </button>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="inline-flex h-11 w-fit items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#3a2762] disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="isSavingWhatsappProfile"
        @click="emit('save')"
      >
        <span
          class="size-4"
          :class="
            isSavingWhatsappProfile
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-save'
          "
        />
        Salvar perfil
      </button>
    </div>
  </LaisCard>

  <LaisCard title="Nome de exibição">
    <div class="grid gap-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p class="text-[13px] font-semibold text-lais-text">
            {{ whatsappGovernance.display_name || 'Sem nome aprovado' }}
          </p>
          <p class="mt-1 text-[12px] text-[#6b7280]">
            Solicitação:
            {{
              whatsappGovernance.requested_display_name ||
              'nenhuma solicitação aberta'
            }}
          </p>
        </div>
        <span
          class="inline-flex items-center rounded-full border px-3 py-1 text-[12px] font-semibold"
          :class="
            whatsappDisplayNameStatusClass(
              whatsappGovernance.display_name_status
            )
          "
        >
          {{
            whatsappDisplayNameStatusLabel(
              whatsappGovernance.display_name_status
            )
          }}
        </span>
      </div>

      <div class="grid gap-3 lg:grid-cols-[1fr_auto]">
        <LaisField label="Novo nome de exibição">
          <input
            v-model="whatsappRequestedDisplayName"
            type="text"
            :class="input"
          />
        </LaisField>
        <button
          type="button"
          class="mt-auto inline-flex h-11 items-center justify-center gap-2 rounded-lais bg-lais-primary px-4 text-[14px] font-semibold text-white transition-colors hover:bg-[#3a2762] disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="isRequestingWhatsappDisplayName"
          @click="emit('requestDisplayName')"
        >
          <span
            class="size-4"
            :class="
              isRequestingWhatsappDisplayName
                ? 'i-lucide-loader-circle animate-spin'
                : 'i-lucide-send'
            "
          />
          Solicitar
        </button>
      </div>

      <p class="text-[12px] leading-relaxed text-[#6b7280]">
        Prazo esperado: 24-48h. Mudanças frequentes de nome ou foto podem exigir
        nova análise da Meta.
      </p>
    </div>
  </LaisCard>
</template>
